import React, { useState, useContext, useEffect, useMemo } from 'react';
import axios from 'axios';
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import { LoginContext } from '../../Contexts/LoginContext';
import { Filter, X, Wallet, TrendingUp, CreditCard, Send, Package, Download } from 'lucide-react';
import PaymentMethods from '../../Components/seller/PaymentMethods';
import Loader from '../../Components/utils/Loader';
import { showToast } from '../../Components/utils/ShowToast';
import { FiInfo } from 'react-icons/fi';
import { formatCurrency } from '../../data/constants';
import { generateAccountStatement, generatePayoutStatement } from '../../utils/documentGenerator';
import Logo from '../../images/pngs/Logo.png';
import PayoutFeeNote from '../../Components/utils/PayoutFeeNote';

const CHART_COLORS = {
  primary: '#ff6b81',
  primaryLight: 'rgba(255, 107, 129, 0.15)',
  success: '#10b981',
  successLight: 'rgba(16, 185, 129, 0.15)',
  warning: '#f59e0b',
  warningLight: 'rgba(245, 158, 11, 0.15)',
  info: '#3b82f6',
  infoLight: 'rgba(59, 130, 246, 0.15)',
  gray: '#6b7280',
};

const AnalyticsPage = () => {
  const { user, currency, exchangeRate, feesObj } = useContext(LoginContext);
  const [showWithdrawalModal, setShowWithdrawalModal] = useState(false);
  const [loadingEarnings, setLoadingEarnings] = useState(true);
  const [loadingHistory, setLoadingHistory] = useState(true);

  const [earnings, setEarnings] = useState({
    availableBalance: 0,
    totalEarnings: 0,
    paidBalance: 0,
    requestedForWithdrawal: 0,
    productsSold: 0,
  });

  const [history, setHistory] = useState([]);
  const [filter, setFilter] = useState('7d');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const today = new Date().toISOString().split('T')[0];

  const fetchEarnings = async () => {
    setLoadingEarnings(true);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_Backend_Host_Name}/api/v1/payments/seller/${user.sellerId._id}/earnings`,
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      if (response.data.success) {
        setEarnings(response.data.earnings);
      }
    } catch (error) {
      console.log(error);
      showToast('Failed to fetch earnings', 'error');
    } finally {
      setLoadingEarnings(false);
    }
  };

  const fetchHistory = async () => {
    setLoadingHistory(true);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_Backend_Host_Name}/api/v1/payments/seller/${user.sellerId._id}`,
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      if (response.data.success) {
        setHistory(response.data.history);
      }
    } catch (error) {
      showToast('Failed to fetch payment history', 'error');
    } finally {
      setLoadingHistory(false);
    }
  };

  useEffect(() => {
    if (!user) return;
    fetchEarnings();
    fetchHistory();
  }, [user]);

  const stats = [
    {
      title: 'Available Balance',
      amount: formatCurrency(earnings.availableBalance, currency, exchangeRate),
      icon: Wallet,
      color: CHART_COLORS.primary,
      bgColor: CHART_COLORS.primaryLight,
      hasWithdraw: true,
    },
    {
      title: 'Total Earnings',
      amount: formatCurrency(earnings.totalEarnings, currency, exchangeRate),
      icon: TrendingUp,
      color: CHART_COLORS.success,
      bgColor: CHART_COLORS.successLight,
    },
    {
      title: 'Paid Out',
      amount: formatCurrency(earnings.paidBalance, currency, exchangeRate),
      icon: CreditCard,
      color: CHART_COLORS.info,
      bgColor: CHART_COLORS.infoLight,
    },
    {
      title: 'Pending Withdrawal',
      amount: formatCurrency(earnings.requestedForWithdrawal, currency, exchangeRate),
      icon: Send,
      color: CHART_COLORS.warning,
      bgColor: CHART_COLORS.warningLight,
    },
    {
      title: 'Products Sold',
      amount: earnings.productsSold.toString().padStart(2, '0'),
      icon: Package,
      color: CHART_COLORS.gray,
      bgColor: 'rgba(107, 114, 128, 0.12)',
      isCount: true,
    },
  ];

  const filteredHistory = useMemo(() => {
    if (!history.length) return [];
    if (filter === 'lifetime') return history;
    if (filter === 'custom') {
      if (!customStartDate || !customEndDate) return history;
      const start = new Date(customStartDate);
      const end = new Date(customEndDate);
      return history.filter((h) => {
        const itemDate = new Date(h.date);
        return itemDate >= start && itemDate <= end;
      });
    }
    let rangeDays;
    if (filter === '7d') rangeDays = 7;
    else if (filter === '30d') rangeDays = 30;
    else if (filter === '90d') rangeDays = 90;
    const cutoff = new Date();
    cutoff.setDate(cutoff.getDate() - rangeDays);
    return history.filter((h) => new Date(h.date) >= cutoff);
  }, [history, filter, customStartDate, customEndDate]);

  // Chart 1: Earnings over time (daily aggregation for Earning entries)
  const earningsOverTimeData = useMemo(() => {
    const earningEntries = filteredHistory.filter((h) => h.status === 'Earning');
    if (!earningEntries.length) return [];

    const byDate = {};
    earningEntries.forEach((h) => {
      const d = new Date(h.date).toLocaleDateString('en-CA', { year: 'numeric', month: 'short', day: 'numeric' });
      byDate[d] = (byDate[d] || 0) + h.amount;
    });

    return Object.entries(byDate)
      .map(([date, amount]) => ({ date, amount }))
      .sort((a, b) => new Date(a.date) - new Date(b.date));
  }, [filteredHistory]);

  // Chart 2: Balance breakdown (pie)
  const balanceBreakdownData = useMemo(() => {
    const data = [
      { name: 'Available', value: Math.max(0, earnings.availableBalance), color: CHART_COLORS.primary },
      { name: 'Paid Out', value: Math.max(0, earnings.paidBalance), color: CHART_COLORS.success },
      { name: 'Pending Withdrawal', value: Math.max(0, earnings.requestedForWithdrawal), color: CHART_COLORS.warning },
    ].filter((d) => d.value > 0);
    return data.length ? data : [{ name: 'No data', value: 1, color: '#e5e7eb' }];
  }, [earnings]);

  // Chart 3: Earnings by payment method (from history)
  const paymentMethodData = useMemo(() => {
    const earningEntries = filteredHistory.filter((h) => h.status === 'Earning');
    const byMethod = {};
    earningEntries.forEach((h) => {
      const m = h.paymentMethod || 'Other';
      byMethod[m] = (byMethod[m] || 0) + h.amount;
    });
    const colors = [CHART_COLORS.primary, CHART_COLORS.info, CHART_COLORS.warning];
    return Object.entries(byMethod).map(([name, value], i) => ({
      name,
      value,
      color: colors[i % colors.length],
    }));
  }, [filteredHistory]);

  // Chart 4: Monthly earnings vs withdrawals (bar)
  const monthlyComparisonData = useMemo(() => {
    const earningEntries = filteredHistory.filter((h) => h.status === 'Earning');
    const withdrawEntries = filteredHistory.filter((h) => h.status === 'Withdraw' || h.status === 'Paid');

    const byMonth = {};
    const addToMonth = (entries, key) => {
      entries.forEach((h) => {
        const d = new Date(h.date);
        const m = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
        const label = d.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
        if (!byMonth[m]) byMonth[m] = { monthKey: m, month: label, earnings: 0, withdrawals: 0 };
        byMonth[m][key] += h.amount;
      });
    };
    addToMonth(earningEntries, 'earnings');
    addToMonth(withdrawEntries, 'withdrawals');

    return Object.values(byMonth)
      .sort((a, b) => a.monthKey.localeCompare(b.monthKey))
      .slice(-6);
  }, [filteredHistory]);

  const handleWithdrawal = async () => {
    setLoadingEarnings(true);
    try {
      // The amount, seller and payout method are all resolved server-side from the
      // authenticated user — nothing about the payout is taken from the client.
      const response = await axios.post(
        `${process.env.REACT_APP_Backend_Host_Name}/api/v1/payments/seller/request-withdrawal`,
        {},
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      if (response.data.success) {
        await fetchEarnings();
        await fetchHistory();
        showToast('Withdrawal request submitted!', 'success');
        setShowWithdrawalModal(false);
      }
    } catch (error) {
      showToast(error.response?.data?.message || 'Something went wrong!', 'error');
      await fetchEarnings();
    } finally {
      setLoadingEarnings(false);
    }
  };

  const getPeriodForGenerator = () => {
    if (filter === '7d') return 'week';
    if (filter === '30d') return 'month';
    if (filter === '90d') return '3month';
    if (filter === 'lifetime') return 'lifetime';
    return 'custom';
  };

  const handleGeneratePayoutPdf = async () => {
    try {
      const opts = {
        currency,
        exchangeRate,
        logoPath: Logo,
        period: getPeriodForGenerator(),
        startDate: customStartDate,
        endDate: customEndDate,
      };
      await generatePayoutStatement(filteredHistory, user?.sellerId || user, opts);
      showToast('Payout Statement generated', 'success');
    } catch (err) {
      showToast('Failed to generate document', 'error');
    }
  };

  const handleGenerateAccountStatement = async () => {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_Backend_Host_Name}/api/v1/orders/seller/current`,
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      const { start, end } = (() => {
        if (filter === 'lifetime') return { start: new Date(0), end: new Date() };
        if (filter === 'custom' && customStartDate && customEndDate)
          return { start: new Date(customStartDate), end: new Date(customEndDate) };
        const end = new Date();
        const start = new Date();
        const days = filter === '7d' ? 7 : filter === '30d' ? 30 : 90;
        start.setDate(start.getDate() - days);
        return { start, end };
      })();
      const orders = (res.data.orders || []).filter((o) => {
        const d = new Date(o.createdAt);
        return d >= start && d <= end;
      });
      const opts = {
        currency,
        exchangeRate,
        logoPath: Logo,
        period: getPeriodForGenerator(),
        startDate: customStartDate,
        endDate: customEndDate,
      };
      // Only a fallback: each order carries the rate it was actually billed at.
      await generateAccountStatement(orders, user?.sellerId || user, opts, { seller: feesObj?.seller ?? 0 });
      showToast('Account Statement generated', 'success');
    } catch (err) {
      showToast('Failed to generate document', 'error');
    }
  };

  const resetFilters = () => {
    setFilter('7d');
    setCustomStartDate('');
    setCustomEndDate('');
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    return (
      <div className="earningsChartTooltip">
        <p className="earningsChartTooltipLabel">{label}</p>
        {payload.map((p) => (
          <p key={p.name} style={{ color: p.color }}>
            {p.name}: {formatCurrency(p.value, currency, exchangeRate)}
          </p>
        ))}
      </div>
    );
  };

  return (
    <div className="earningsPage">
      <section className="earningsMainSection">
        <header className="earningsPageHeader">
          <h1 className="earningsPageTitle">Earnings</h1>
          <p className="earningsPageSubtitle">Manage your balance, payment methods, and view analytics</p>
        </header>

        <div className="earningsPaymentSection">
          <PaymentMethods />
        </div>

        <div className="earningsStatsGrid">
          {stats.map((stat, index) => (
            <div key={index} className="earningsStatCard">
              <div className="earningsStatIcon" style={{ background: stat.bgColor, color: stat.color }}>
                <stat.icon size={22} strokeWidth={2} />
              </div>
              <div className="earningsStatContent">
                <span className="earningsStatLabel">{stat.title}</span>
                <span className="earningsStatAmount">{stat.amount}</span>
                {stat.hasWithdraw && (
                  <button
                    className="earningsWithdrawBtn"
                    onClick={() => setShowWithdrawalModal(true)}
                    disabled={earnings.availableBalance < 30 || !user?.sellerId?.activePaymentMethod}
                  >
                    Withdraw
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>

        {(earnings.availableBalance < 30 || !user?.sellerId?.activePaymentMethod) && (
          <div className="earningsInfoBanner">
            <FiInfo className="earningsInfoIcon" />
            To withdraw, your account should have at least {formatCurrency(30, currency, exchangeRate)} in available
            balance and an active payment method.
          </div>
        )}

        {/* Charts Section */}
        <div className="earningsChartsSection">
          <div className="earningsChartCard earningsChartCardWide">
            <h3 className="earningsChartTitle">Earnings Over Time</h3>
            {earningsOverTimeData.length > 0 ? (
              <ResponsiveContainer width="100%" height={280}>
                <AreaChart data={earningsOverTimeData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                  <defs>
                    <linearGradient id="earningsGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={CHART_COLORS.primary} stopOpacity={0.4} />
                      <stop offset="100%" stopColor={CHART_COLORS.primary} stopOpacity={0.05} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis dataKey="date" tick={{ fontSize: 11 }} stroke="#9ca3af" />
                  <YAxis tick={{ fontSize: 11 }} stroke="#9ca3af" tickFormatter={(v) => formatCurrency(v, currency, exchangeRate)} />
                  <Tooltip content={<CustomTooltip />} />
                  <Area type="monotone" dataKey="amount" stroke={CHART_COLORS.primary} strokeWidth={2} fill="url(#earningsGradient)" name="Earnings" />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <div className="earningsChartEmpty">No earnings data for the selected period</div>
            )}
          </div>

          <div className="earningsChartsRow">
            <div className="earningsChartCard">
              <h3 className="earningsChartTitle">Balance Breakdown</h3>
              {balanceBreakdownData[0]?.name !== 'No data' ? (
                <ResponsiveContainer width="100%" height={220}>
                  <PieChart>
                    <Pie
                      data={balanceBreakdownData}
                      cx="50%"
                      cy="50%"
                      innerRadius={50}
                      outerRadius={80}
                      paddingAngle={2}
                      dataKey="value"
                      nameKey="name"
                      label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                    >
                      {balanceBreakdownData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(v) => formatCurrency(v, currency, exchangeRate)} />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div className="earningsChartEmpty">No balance data</div>
              )}
            </div>

            <div className="earningsChartCard">
              <h3 className="earningsChartTitle">Earnings by Payment Method</h3>
              {paymentMethodData.length > 0 ? (
                <ResponsiveContainer width="100%" height={220}>
                  <PieChart>
                    <Pie
                      data={paymentMethodData}
                      cx="50%"
                      cy="50%"
                      innerRadius={50}
                      outerRadius={80}
                      paddingAngle={2}
                      dataKey="value"
                      nameKey="name"
                    >
                      {paymentMethodData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(v) => formatCurrency(v, currency, exchangeRate)} />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div className="earningsChartEmpty">No payment data for period</div>
              )}
            </div>
          </div>

          <div className="earningsChartCard earningsChartCardWide">
            <h3 className="earningsChartTitle">Monthly Earnings vs Withdrawals</h3>
            {monthlyComparisonData.length > 0 ? (
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={monthlyComparisonData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis dataKey="month" tick={{ fontSize: 11 }} stroke="#9ca3af" />
                  <YAxis tick={{ fontSize: 11 }} stroke="#9ca3af" tickFormatter={(v) => formatCurrency(v, currency, exchangeRate)} />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend />
                  <Bar dataKey="earnings" name="Earnings" fill={CHART_COLORS.primary} radius={[4, 4, 0, 0]} />
                  <Bar dataKey="withdrawals" name="Withdrawals" fill={CHART_COLORS.info} radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="earningsChartEmpty">No monthly data for the selected period</div>
            )}
          </div>
        </div>

        {/* Sales List */}
        <div className="earningsSalesSection">
          <div className="earningsSalesHeader">
            <h2 className="earningsSalesTitle">Transaction History</h2>
            <div className="earningsSalesFilters">
              <div className="earningsFilterGroup">
                <select className="earningsFilterSelect" value={filter} onChange={(e) => setFilter(e.target.value)}>
                  <option value="7d">Last 7 days</option>
                  <option value="30d">Last 30 days</option>
                  <option value="90d">Last 90 days</option>
                  <option value="lifetime">Lifetime</option>
                  <option value="custom">Custom</option>
                </select>
                {filter === 'custom' && (
                  <div className="earningsCustomDates">
                    <input type="date" value={customStartDate} onChange={(e) => setCustomStartDate(e.target.value)} max={today} />
                    <input type="date" value={customEndDate} onChange={(e) => setCustomEndDate(e.target.value)} max={today} />
                  </div>
                )}
              </div>
              <div className="earningsFilterActions">
                <button type="button" className="earningsFilterBtn earningsFilterBtnSecondary" onClick={resetFilters}>
                  <X size={16} />
                  Reset
                </button>
                <button
                  type="button"
                  className="earningsFilterBtn earningsFilterBtnPrimary"
                  onClick={handleGenerateAccountStatement}
                  disabled={!history.length}
                >
                  <Download size={16} />
                  Account Statement
                </button>
                <button
                  type="button"
                  className="earningsFilterBtn earningsFilterBtnPrimary"
                  onClick={handleGeneratePayoutPdf}
                  disabled={!filteredHistory.length}
                >
                  <Download size={16} />
                  Payout Statement
                </button>
              </div>
            </div>
          </div>

          <div className="earningsTableWrapper">
            <table className="earningsTable">
              <thead>
                <tr>
                  <th>Activity</th>
                  <th>Order ID</th>
                  <th>Buyer</th>
                  <th>Date</th>
                  <th>Payment Method</th>
                  <th>Amount</th>
                </tr>
              </thead>
              <tbody>
                {filteredHistory.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="earningsTableEmpty">
                      No transactions for the selected period
                    </td>
                  </tr>
                ) : (
                  filteredHistory.map((item, index) => (
                    <tr key={index}>
                      <td>{item.description}</td>
                      <td>{item.orderIdCustom || '–'}</td>
                      <td>{item.buyerUsername || '–'}</td>
                      <td>{new Date(item.date).toLocaleDateString()}</td>
                      <td>{item.paymentMethod || '–'}</td>
                      <td className="earningsTableAmount">{formatCurrency(item.amount, currency, exchangeRate)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {showWithdrawalModal && (
          <div className="modalOverlay" onClick={() => setShowWithdrawalModal(false)}>
            <div className="modalContent earningsWithdrawModal" onClick={(e) => e.stopPropagation()}>
              <h2 className="modalTitle">Withdraw Payment</h2>
              <div className="modalBody">
                {user?.sellerId?.activePaymentMethod ? (
                  <>
                    <p>
                      Active Method: <strong>{user.sellerId.activePaymentMethod}</strong>
                    </p>
                    <p>You are about to withdraw {formatCurrency(earnings.availableBalance, currency, exchangeRate)}.</p>
                    <PayoutFeeNote variant="withdrawal" />
                  </>
                ) : (
                  <p>No active payment method configured.</p>
                )}
              </div>
              <div className="modalActions">
                <button type="button" className="earningsModalBtn earningsModalBtnCancel" onClick={() => setShowWithdrawalModal(false)} disabled={loadingEarnings}>
                  Cancel
                </button>
                <button
                  type="button"
                  className="earningsModalBtn earningsModalBtnConfirm"
                  onClick={handleWithdrawal}
                  disabled={!user?.sellerId?.activePaymentMethod || earnings.availableBalance < 30 || loadingEarnings}
                >
                  {loadingEarnings ? <Loader type="miniPlus" /> : `Withdraw ${formatCurrency(earnings.availableBalance, currency, exchangeRate)}`}
                </button>
              </div>
            </div>
          </div>
        )}

        {(loadingEarnings || loadingHistory) && <Loader type="checkmate" />}
      </section>
    </div>
  );
};

export default AnalyticsPage;
